import React, { useEffect, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Stack,
  Button,
  GlobalStyles,
  Fade,
  Paper,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import localization from "../utils/localization";
import logo256 from "../assets/logo_256.png";

export function Terms() {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: localization.strings.termsSection1Title,
      content: localization.strings.termsSection1Content,
    },
    {
      title: localization.strings.termsSection2Title,
      content: localization.strings.termsSection2Content,
    },
    {
      title: localization.strings.termsSection3Title,
      content: localization.strings.termsSection3Content,
    },
  ];

  return (
    <Box
      ref={containerRef}
      sx={{
        backgroundColor: "#F8FAFC",
        width: "100%",
        height: "100%",
        overflowY: "auto",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <GlobalStyles
        styles={{
          body: { margin: 0, padding: 0 },
        }}
      />

      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: "pointer" }}
              onClick={() => navigate("/")}
            >
              <Box
                sx={{
                  p: "2px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)",
                  boxShadow: "0 8px 16px rgba(30, 136, 229, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={logo256}
                  alt="EasyPass Logo"
                  sx={{
                    display: "block",
                    height: "2rem",
                    width: "2rem",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    p: "1px",
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: "#1E293B",
                  ml: 2,
                  letterSpacing: "-0.03em",
                  fontSize: "1.2rem",
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                EasyPass
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small">
                <Select
                  value={localization.language.lang || "en"}
                  onChange={(event) => {
                    window.location.href = "?lang=" + event.target.value;
                  }}
                  sx={{
                    color: "#1E293B",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    backgroundColor: "rgba(0,0,0,0.04)",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    height: "2.4rem",
                  }}
                >
                  <MenuItem value="en">EN</MenuItem>
                  <MenuItem value="zh">ZH</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="text"
                onClick={() => navigate("/")}
                sx={{
                  color: "#64748B",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { color: "#1E88E5" },
                }}
              >
                {localization.strings.goBack}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Fade in={true} timeout={800}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                color: "#1E293B",
                fontWeight: 800,
                mb: 2,
                fontFamily: "'Poppins', sans-serif",
                textAlign: "center",
              }}
            >
              {localization.strings.termsTitle}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: "#64748B",
                fontSize: "1.1rem",
                textAlign: "center",
                mb: 6,
              }}
            >
              {localization.strings.termsSubTitle}
            </Typography>

            <Stack spacing={4}>
              {sections.map((section, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: "24px",
                    border: "1px solid rgba(0,0,0,0.06)",
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#1E293B",
                      fontWeight: 700,
                      mb: 2,
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#475569",
                      lineHeight: 1.8,
                      fontSize: "1.05rem",
                    }}
                  >
                    {section.content}
                  </Typography>
                </Paper>
              ))}
            </Stack>

            <Box sx={{ mt: 8, textAlign: "center", borderTop: "1px solid rgba(0,0,0,0.06)", pt: 4 }}>
              <Typography variant="body2" sx={{ color: "#94A3B8" }}>
                Last updated: June 6, 2026
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}

export default Terms;
